import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { getUrineTestResults, updateLabData } from '../services/labService';
import MainHeader from '../Components/MainHeader';
import CustomAlert from '../Components/CustomAlert';
import { useCustomAlert } from '../hooks/useCustomAlert';
import UrineTestFormula from '../formula/UrineTest';

const TestResultsContainer = styled.div`
  min-height: 100vh;
  background-color: #f8f9fa;
  font-family: 'Noto Sans Thai', sans-serif !important;
  * {
    font-family: 'Noto Sans Thai', sans-serif !important;
    font-weight: normal;
    color: #666666;
  }
`;

const TopBar = styled.div`
  background-color: #00BBD3;
  padding: 16px 24px;
  color: white;
  font-size: 20px;
  font-weight: bold;
  font-family: 'Noto Sans Thai', sans-serif !important;
  max-width: 480px;
  margin: 0 auto;
  width: 100%;
  box-sizing: border-box;
  text-align: left;
`;

const Header = styled.div`
  background-color: white;
  padding: 16px 24px;
  color: #666;
  font-size: 18px;
  font-weight: bold;
  font-family: 'Noto Sans Thai', sans-serif !important;
  max-width: 480px;
  margin: 0 auto;
  width: 100%;
  box-sizing: border-box;
`;

const BackButton = styled.button`
  background: none;
  border: none;
  color: #666;
  font-size: 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 0;
  margin-bottom: 8px;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const PageTitle = styled.h1`
  font-size: 24px;
  font-weight: bold;
  color: #333;
  margin: 0;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const StatusCard = styled.div`
  background: ${props => props.$bgColor || '#34A853'};
  border-radius: 16px;
  padding: 20px;
  margin-bottom: 16px;
  color: white;
  display: flex;
  align-items: center;
  gap: 16px;
`;

const UrineIcon = styled.img`
  width: 60px;
  height: 60px;
  flex-shrink: 0;
  object-fit: contain;
`;

const StatusInfo = styled.div`
  flex: 1;
`;

const StatusTitle = styled.div`
  font-size: 16px;
  font-weight: bold;
  color: white;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const Content = styled.div`
  padding: 24px;
  max-width: 480px;
  margin: 0 auto;
  width: 100%;
  box-sizing: border-box;
`;

const ResultCard = styled.div`
  background-color: white;
  border-radius: 5px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  overflow: hidden;
  margin-bottom: 24px;
`;

const CardHeader = styled.div`
  background-color: #00BBD3;
  padding: 16px 20px;
  color: white;
  text-align: center;
`;

const HeaderText = styled.h3`
  font-size: 18px;
  font-weight: bold;
  margin: 0;
  color: white;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const CardContent = styled.div`
  padding: 20px;
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const InfoLabel = styled.span`
  font-size: 16px;
  color: #666666;
  font-family: 'Noto Sans Thai', sans-serif !important;
  font-weight: ${props => props.bold ? 'bold' : 'normal'};
  flex: 1;
`;

const InfoValue = styled.span`
  font-size: 16px;
  color: #666666;
  font-family: 'Noto Sans Thai', sans-serif !important;
  font-weight: bold;
  text-align: right;
  flex: 1;
`;

const Divider = styled.hr`
  border: none;
  height: 1px;
  background-color: #e1e5e9;
  margin: 16px 0;
`;

const SectionTitle = styled.h4`
  font-size: 16px;
  font-weight: bold;
  color: #333333;
  margin: 20px 0 12px 0;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

// ตาราง + ไอคอนดินสอ styled components
const HistorySection = styled.div`
  background-color: white;
  border-radius: 16px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
`;

const HistoryTitle = styled.h3`
  font-size: 18px;
  font-weight: bold;
  color: #333;
  margin-bottom: 16px;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHeaderStyled = styled.thead`
  background-color: #00BBD3;
  color: white;
`;

const TableHeaderCell = styled.th`
  padding: 12px 8px;
  text-align: center;
  font-size: 14px;
  font-weight: bold;
  font-family: 'Noto Sans Thai', sans-serif !important;
  color: white;
`;

const TableRow = styled.tr`
  border-bottom: 1px solid #e0e0e0;

  &:last-child {
    border-bottom: none;
  }
`;

const TableCell = styled.td`
  padding: 12px 8px;
  text-align: center;
  font-size: 14px;
  color: #666;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const EditButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;

  svg {
    width: 20px;
    height: 20px;
    fill: #34A853;
  }

  &:hover svg {
    fill: #45a049;
  }
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 16px;
  padding: 24px;
  max-width: 400px;
  width: 90%;
  max-height: 80vh;
  overflow-y: auto;
`;

const ModalTitle = styled.h3`
  font-size: 18px;
  font-weight: bold;
  color: #333;
  margin-bottom: 20px;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const FormGroup = styled.div`
  margin-bottom: 16px;
`;

const Label = styled.label`
  display: block;
  font-size: 14px;
  color: #666;
  margin-bottom: 8px;
  font-family: 'Noto Sans Thai', sans-serif !important;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  font-size: 14px;
  font-family: 'Noto Sans Thai', sans-serif !important;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #00BBD3;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 24px;
`;

const Button = styled.button`
  flex: 1;
  padding: 12px;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: bold;
  cursor: pointer;
  font-family: 'Noto Sans Thai', sans-serif !important;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.9;
  }
`;

const SaveButton = styled(Button)`
  background-color: #00BBD3;
  color: white;
`;

const CancelButton = styled(Button)`
  background-color: #e0e0e0;
  color: #666;
`;

const RadioGroup = styled.div`
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
`;

const RadioOption = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const RadioButton = styled.input.attrs({ type: 'radio' })`
  width: 18px;
  height: 18px;
  cursor: pointer;
`;

const RadioLabel = styled.label`
  font-size: 14px;
  color: #666;
  cursor: pointer;
  font-family: 'Noto Sans Thai', sans-serif;
`;

const Select = styled.select`
  width: 100%;
  padding: 10px 12px;
  border: 2px solid #e1e5e9;
  border-radius: 8px;
  font-size: 14px;
  color: #666;
  font-family: 'Noto Sans Thai', sans-serif;
  background-color: white;
  cursor: pointer;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #00BBD3;
  }
`;

const formatDisplayDate = (dateString) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return dateString;
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = (date.getFullYear() + 543).toString().slice(-2);
  return `${day}/${month}/${year}`;
};

// DB name → display label mapping
const URINE_LABEL_MAP = {
  'urinalysis_color': 'สีของปัสสาวะ',
  'urinalysis_appearance': 'ความใส',
  'urinalysis_ph': 'pH',
  'urinalysis_specific_gravity': 'ความถ่วงจำเพาะ',
  'urinalysis_protein': 'โปรตีน',
  'urinalysis_glucose': 'น้ำตาล',
  'urinalysis_ketone': 'คีโตน',
  'urinalysis_blood': 'เลือด',
  'urinalysis_leukocytes': 'เม็ดเลือดขาว (Leukocytes)',
  'urinalysis_nitrite': 'ไนเตรต',
  'urinalysis_bilirubin': 'บิลิรูบิน',
  'urinalysis_urobilinogen': 'ยูโรบิลิโนเจน',
  'urinalysis_rbc': 'เซลล์เม็ดเลือดแดง',
  'urinalysis_wbc': 'เซลล์เม็ดเลือดขาว',
  'urinalysis_bacteria': 'แบคทีเรีย',
  'urinalysis_epithelial': 'เซลล์เยื่อบุผิว (Epithelial)'
};

// field key → DB name for update API
const URINE_DB_NAME_MAP = {
  'urinalysis_color': 'urinalysiscolorresul',
  'urinalysis_appearance': 'urinalysisapperancer',
  'urinalysis_ph': 'urinalysisphresult',
  'urinalysis_specific_gravity': 'urinalysisspecificgr',
  'urinalysis_protein': 'urinalysisproteinres',
  'urinalysis_glucose': 'urinalysissugarresul',
  'urinalysis_ketone': 'urinalysisketoneresu',
  'urinalysis_blood': 'urinalysisbloodresul',
  'urinalysis_leukocytes': 'urinalysisleukocyter',
  'urinalysis_nitrite': 'urinalysisnitritere',
  'urinalysis_bilirubin': 'urinalysisbilirubinr',
  'urinalysis_urobilinogen': 'urinalysisurobilino',
  'urinalysis_rbc': 'urinalysisrbcresult',
  'urinalysis_wbc': 'urinalysiswbcresult',
  'urinalysis_bacteria': 'urinalysisbactcellre',
  'urinalysis_epithelial': 'urinalysisepithelial'
};

const UrineTestResultsPage = () => {
  const navigate = useNavigate();
  const { alertState, showAlert, closeAlert } = useCustomAlert();
  const [urineResults, setUrineResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusColor, setStatusColor] = useState('#e0e0e0');
  const [abnormalList, setAbnormalList] = useState([]);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [editForm, setEditForm] = useState({});

  const loadUrineResults = async () => {
    try {
      setLoading(true);

      const response = await getUrineTestResults();
      console.log('🔬 Urine Test Response:', response);

      if (response?.result && Array.isArray(response.list)) {
        const formattedResults = response.list.map(item => {
          const results = {};
          Object.entries(URINE_LABEL_MAP).forEach(([key, label]) => {
            if (item[key]) {
              results[label] = item[key];
            }
          });

          return {
            date: item.visit_date || item.dateadd || '-',
            hospital: item.hospital_name || 'โรงพยาบาล',
            results: results,
            rawData: item
          };
        });

        console.log('🔬 Formatted Urine Results:', formattedResults);

        // Sort by date (newest first) to ensure latest is first
        const sortedResults = [...formattedResults].sort((a, b) => {
          const dateA = new Date(a.date);
          const dateB = new Date(b.date);
          return dateB - dateA; // newest first
        });

        setUrineResults(sortedResults);

        if (sortedResults.length > 0) {
          // Get the latest (first) record
          const latestRecord = sortedResults[0];
          const latestItem = latestRecord.rawData;
          console.log('🔬 Latest Urine Record (for status card):', latestRecord);
          const urineData = {
            ph: latestItem.urinalysis_ph,
            color: latestItem.urinalysis_color,
            clarity: latestItem.urinalysis_appearance,
            specificGravity: latestItem.urinalysis_specific_gravity,
            protein: latestItem.urinalysis_protein,
            glucose: latestItem.urinalysis_glucose,
            ketones: latestItem.urinalysis_ketone,
            blood: latestItem.urinalysis_blood,
            leukocytes: latestItem.urinalysis_leukocytes,
            nitrite: latestItem.urinalysis_nitrite,
            bilirubin: latestItem.urinalysis_bilirubin,
            urobilinogen: latestItem.urinalysis_urobilinogen,
            redBloodCells: latestItem.urinalysis_rbc,
            whiteBloodCells: latestItem.urinalysis_wbc,
            bacteria: latestItem.urinalysis_bacteria,
            epithelialCells: latestItem.urinalysis_epithelial
          };

          const formulaResult = UrineTestFormula(urineData);
          const colorMap = {
            'green': '#34A853',
            'red': '#ff4757',
            'gray': '#e0e0e0'
          };
          setStatusColor(colorMap[formulaResult.color] || '#e0e0e0');
          setAbnormalList(formulaResult.abnormalFindings || []);
        }
      }
    } catch (error) {
      console.error('Error loading urine results:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUrineResults();
  }, []);

  const handleEditClick = (record) => {
    setEditingRecord(record);
    const form = {};
    Object.keys(URINE_LABEL_MAP).forEach(key => {
      form[key] = record.rawData[key] || '';
    });
    setEditForm(form);
    setShowEditModal(true);
  };

  const handleEditSubmit = async () => {
    try {
      const updates = [];

      Object.keys(URINE_LABEL_MAP).forEach(key => {
        if (editForm[key] && editForm[key] !== editingRecord.rawData[key]) {
          const dbName = URINE_DB_NAME_MAP[key];
          if (dbName) {
            // ใช้ rawData.dateadd ที่มีเวลาครบ แทน date ที่เป็นแค่วันที่
            updates.push(updateLabData(editingRecord.rawData.dateadd, dbName, editForm[key], 'h'));
          }
        }
      });

      if (updates.length > 0) {
        await Promise.all(updates);
      }

      showAlert('อัปเดตข้อมูลเรียบร้อยแล้ว');
      setShowEditModal(false);
      setEditingRecord(null);
      await loadUrineResults();
    } catch (error) {
      console.error('Error updating urine data:', error);
      showAlert('เกิดข้อผิดพลาดในการอัปเดตข้อมูล');
    }
  };

  const handleModalClose = () => {
    setShowEditModal(false);
    setEditingRecord(null);
  };

  return (
    <TestResultsContainer>
      <MainHeader />

      <TopBar>ผลตรวจปัสสาวะ</TopBar>

      <Header>
        <BackButton onClick={() => navigate('/dashboard')}>
          &lt; ย้อนกลับ
        </BackButton>
        <PageTitle>ผลตรวจปัสสาวะ (Urine Test)</PageTitle>
      </Header>

      <Content>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
            กำลังโหลดข้อมูล...
          </div>
        ) : urineResults.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
            ไม่พบข้อมูลผลตรวจปัสสาวะ
          </div>
        ) : (
          <>
            {/* Status Card */}
            <StatusCard $bgColor={statusColor}>
              <UrineIcon src="./ผลการตรวจปัสสาวะสี.png" alt="ผลตรวจปัสสาวะ" />
              <StatusInfo>
                <StatusTitle style={{ color: 'white' }}>
                  {abnormalList.length > 0
                    ? `ค่าที่ผิดปกติ: ${abnormalList.join(', ')}`
                    : 'ผลตรวจปกติ'}
                </StatusTitle>
              </StatusInfo>
            </StatusCard>

            {urineResults.map((result, index) => (
            <ResultCard key={index}>
              <CardHeader>
                <HeaderText>ผลตรวจปัสสาวะ</HeaderText>
              </CardHeader>

              <CardContent>
                <InfoRow>
                  <InfoLabel bold>วันที่ตรวจ</InfoLabel>
                  <InfoValue>{formatDisplayDate(result.date)}</InfoValue>
                </InfoRow>
                <InfoRow>
                  <InfoLabel bold>ตรวจที่</InfoLabel>
                  <InfoValue>{result.hospital}</InfoValue>
                </InfoRow>

                <Divider />

                <SectionTitle>รายการตรวจ</SectionTitle>

                {Object.entries(result.results).map(([label, value], idx) => (
                  <InfoRow key={idx}>
                    <InfoLabel>{label}</InfoLabel>
                    <InfoValue>{value}</InfoValue>
                  </InfoRow>
                ))}
              </CardContent>
            </ResultCard>
          ))}

            {/* ตารางประวัติทั้งหมด */}
            <HistorySection>
              <HistoryTitle>ประวัติการตรวจทั้งหมด</HistoryTitle>
              <Table>
                <TableHeaderStyled>
                  <tr>
                    <TableHeaderCell>วันที่ตรวจ</TableHeaderCell>
                    <TableHeaderCell>ค่าที่วัด</TableHeaderCell>
                    <TableHeaderCell>ความเสี่ยง</TableHeaderCell>
                    <TableHeaderCell></TableHeaderCell>
                  </tr>
                </TableHeaderStyled>
                <tbody>
                  {urineResults.map((record, index) => {
                    const values = Object.entries(record.results)
                      .map(([label, value]) => `${label}: ${value}`)
                      .join(', ');

                    // คำนวณความเสี่ยง
                    const raw = record.rawData;
                    const urineData = {
                      ph: raw.urinalysis_ph,
                      color: raw.urinalysis_color,
                      clarity: raw.urinalysis_appearance,
                      specificGravity: raw.urinalysis_specific_gravity,
                      protein: raw.urinalysis_protein,
                      glucose: raw.urinalysis_glucose,
                      ketones: raw.urinalysis_ketone,
                      blood: raw.urinalysis_blood,
                      leukocytes: raw.urinalysis_leukocytes,
                      nitrite: raw.urinalysis_nitrite,
                      bilirubin: raw.urinalysis_bilirubin,
                      urobilinogen: raw.urinalysis_urobilinogen,
                      redBloodCells: raw.urinalysis_rbc,
                      whiteBloodCells: raw.urinalysis_wbc,
                      bacteria: raw.urinalysis_bacteria,
                      epithelialCells: raw.urinalysis_epithelial
                    };
                    const formulaResult = UrineTestFormula(urineData);
                    const abnormals = formulaResult?.abnormalFindings || [];
                    const riskStatus = abnormals.length > 0 ? `ผิดปกติ (${abnormals.join(', ')})` : 'ปกติ';
                    const riskColor = abnormals.length > 0 ? '#ff4757' : '#34A853';

                    return (
                      <TableRow key={index}>
                        <TableCell>{formatDisplayDate(record.date)}</TableCell>
                        <TableCell style={{ textAlign: 'left', fontSize: '12px' }}>{values || '-'}</TableCell>
                        <TableCell style={{ color: riskColor, fontWeight: 'bold' }}>{riskStatus}</TableCell>
                        <TableCell>
                          <EditButton onClick={() => handleEditClick(record)}>
                            <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                              <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/>
                            </svg>
                          </EditButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </tbody>
              </Table>
            </HistorySection>
          </>
        )}
      </Content>

      {/* Edit Modal */}
      {showEditModal && editingRecord && (
        <Modal onClick={handleModalClose}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalTitle>แก้ไขข้อมูล</ModalTitle>

            <FormGroup>
              <Label>วันที่: {formatDisplayDate(editingRecord.date)}</Label>
            </FormGroup>

            <FormGroup>
              <Label>สีของปัสสาวะ</Label>
              <RadioGroup>
                <RadioOption>
                  <RadioButton
                    id="color-yellow"
                    name="urineColor"
                    value="Yellow"
                    checked={editForm.urinalysis_color === 'Yellow'}
                    onChange={(e) => setEditForm({ ...editForm, urinalysis_color: e.target.value })}
                  />
                  <RadioLabel htmlFor="color-yellow">Yellow (ปกติ)</RadioLabel>
                </RadioOption>
                <RadioOption>
                  <RadioButton
                    id="color-abnormal"
                    name="urineColor"
                    value="abnormal"
                    checked={editForm.urinalysis_color === 'abnormal'}
                    onChange={(e) => setEditForm({ ...editForm, urinalysis_color: e.target.value })}
                  />
                  <RadioLabel htmlFor="color-abnormal">ผิดปกติ</RadioLabel>
                </RadioOption>
              </RadioGroup>
            </FormGroup>

            <FormGroup>
              <Label>ความใส</Label>
              <RadioGroup>
                <RadioOption>
                  <RadioButton
                    id="appearance-clear"
                    name="urineAppearance"
                    value="Clear"
                    checked={editForm.urinalysis_appearance === 'Clear'}
                    onChange={(e) => setEditForm({ ...editForm, urinalysis_appearance: e.target.value })}
                  />
                  <RadioLabel htmlFor="appearance-clear">Clear</RadioLabel>
                </RadioOption>
                <RadioOption>
                  <RadioButton
                    id="appearance-abnormal"
                    name="urineAppearance"
                    value="abnormal"
                    checked={editForm.urinalysis_appearance === 'abnormal'}
                    onChange={(e) => setEditForm({ ...editForm, urinalysis_appearance: e.target.value })}
                  />
                  <RadioLabel htmlFor="appearance-abnormal">ผิดปกติ</RadioLabel>
                </RadioOption>
              </RadioGroup>
            </FormGroup>

            <FormGroup>
              <Label>pH</Label>
              <Input
                type="number"
                step="0.1"
                value={editForm.urinalysis_ph}
                onChange={(e) => setEditForm({ ...editForm, urinalysis_ph: e.target.value })}
                placeholder=""
              />
            </FormGroup>

            <FormGroup>
              <Label>ความถ่วงจำเพาะ</Label>
              <Input
                type="number"
                step="0.001"
                value={editForm.urinalysis_specific_gravity}
                onChange={(e) => setEditForm({ ...editForm, urinalysis_specific_gravity: e.target.value })}
                placeholder=""
              />
            </FormGroup>

            <FormGroup>
              <Label>โปรตีน</Label>
              <Select
                value={editForm.urinalysis_protein || ''}
                onChange={(e) => setEditForm({ ...editForm, urinalysis_protein: e.target.value })}
              >
                <option value="">-- เลือก --</option>
                <option value="Negative">Negative</option>
                <option value="Trace">Trace</option>
                <option value="1+">1+</option>
                <option value="2+">2+</option>
                <option value="3+">3+</option>
                <option value="4+">4+</option>
              </Select>
            </FormGroup>

            <FormGroup>
              <Label>น้ำตาล</Label>
              <Select
                value={editForm.urinalysis_glucose || ''}
                onChange={(e) => setEditForm({ ...editForm, urinalysis_glucose: e.target.value })}
              >
                <option value="">-- เลือก --</option>
                <option value="Negative">Negative</option>
                <option value="Trace">Trace</option>
                <option value="1+">1+</option>
                <option value="2+">2+</option>
                <option value="3+">3+</option>
                <option value="4+">4+</option>
              </Select>
            </FormGroup>

            <FormGroup>
              <Label>คีโตน</Label>
              <Select
                value={editForm.urinalysis_ketone || ''}
                onChange={(e) => setEditForm({ ...editForm, urinalysis_ketone: e.target.value })}
              >
                <option value="">-- เลือก --</option>
                <option value="Negative">Negative</option>
                <option value="Trace">Trace</option>
                <option value="1+">1+</option>
                <option value="2+">2+</option>
                <option value="3+">3+</option>
                <option value="4+">4+</option>
              </Select>
            </FormGroup>

            <FormGroup>
              <Label>เลือด</Label>
              <Select
                value={editForm.urinalysis_blood || ''}
                onChange={(e) => setEditForm({ ...editForm, urinalysis_blood: e.target.value })}
              >
                <option value="">-- เลือก --</option>
                <option value="Negative">Negative</option>
                <option value="Trace">Trace</option>
                <option value="1+">1+</option>
                <option value="2+">2+</option>
                <option value="3+">3+</option>
                <option value="4+">4+</option>
              </Select>
            </FormGroup>

            <FormGroup>
              <Label>เม็ดเลือดขาว (Leukocytes)</Label>
              <Select
                value={editForm.urinalysis_leukocytes || ''}
                onChange={(e) => setEditForm({ ...editForm, urinalysis_leukocytes: e.target.value })}
              >
                <option value="">-- เลือก --</option>
                <option value="Negative">Negative</option>
                <option value="Trace">Trace</option>
                <option value="1+">1+</option>
                <option value="2+">2+</option>
                <option value="3+">3+</option>
                <option value="4+">4+</option>
              </Select>
            </FormGroup>

            <FormGroup>
              <Label>ไนเตรต</Label>
              <Select
                value={editForm.urinalysis_nitrite || ''}
                onChange={(e) => setEditForm({ ...editForm, urinalysis_nitrite: e.target.value })}
              >
                <option value="">-- เลือก --</option>
                <option value="Negative">Negative</option>
                <option value="Trace">Trace</option>
                <option value="1+">1+</option>
                <option value="2+">2+</option>
                <option value="3+">3+</option>
                <option value="4+">4+</option>
              </Select>
            </FormGroup>

            <FormGroup>
              <Label>บิลิรูบิน</Label>
              <Select
                value={editForm.urinalysis_bilirubin || ''}
                onChange={(e) => setEditForm({ ...editForm, urinalysis_bilirubin: e.target.value })}
              >
                <option value="">-- เลือก --</option>
                <option value="Negative">Negative</option>
                <option value="Trace">Trace</option>
                <option value="1+">1+</option>
                <option value="2+">2+</option>
                <option value="3+">3+</option>
                <option value="4+">4+</option>
              </Select>
            </FormGroup>

            <FormGroup>
              <Label>ยูโรบิลิโนเจน</Label>
              <Select
                value={editForm.urinalysis_urobilinogen || ''}
                onChange={(e) => setEditForm({ ...editForm, urinalysis_urobilinogen: e.target.value })}
              >
                <option value="">-- เลือก --</option>
                <option value="Negative">Negative</option>
                <option value="Trace">Trace</option>
                <option value="1+">1+</option>
                <option value="2+">2+</option>
                <option value="3+">3+</option>
                <option value="4+">4+</option>
              </Select>
            </FormGroup>

            <FormGroup>
              <Label>เซลล์เม็ดเลือดแดง</Label>
              <Select
                value={editForm.urinalysis_rbc || ''}
                onChange={(e) => setEditForm({ ...editForm, urinalysis_rbc: e.target.value })}
              >
                <option value="">-- เลือก --</option>
                <option value="0-1">0-1</option>
                <option value="1-2">1-2</option>
                <option value="2-3">2-3</option>
                <option value="3-5">3-5</option>
                <option value="5-10">5-10</option>
                <option value="10-20">10-20</option>
                <option value="20-30">20-30</option>
                <option value="30-50">30-50</option>
                <option value="50-100">50-100</option>
                <option value="100-200">100-200</option>
                <option value=">200">&gt;200</option>
              </Select>
            </FormGroup>

            <FormGroup>
              <Label>เซลล์เม็ดเลือดขาว</Label>
              <Select
                value={editForm.urinalysis_wbc || ''}
                onChange={(e) => setEditForm({ ...editForm, urinalysis_wbc: e.target.value })}
              >
                <option value="">-- เลือก --</option>
                <option value="0-1">0-1</option>
                <option value="1-2">1-2</option>
                <option value="2-3">2-3</option>
                <option value="3-5">3-5</option>
                <option value="5-10">5-10</option>
                <option value="10-20">10-20</option>
                <option value="20-30">20-30</option>
                <option value="30-50">30-50</option>
                <option value="50-100">50-100</option>
                <option value="100-200">100-200</option>
                <option value=">200">&gt;200</option>
              </Select>
            </FormGroup>

            <FormGroup>
              <Label>แบคทีเรีย</Label>
              <Select
                value={editForm.urinalysis_bacteria || ''}
                onChange={(e) => setEditForm({ ...editForm, urinalysis_bacteria: e.target.value })}
              >
                <option value="">-- เลือก --</option>
                <option value="Negative">Negative</option>
                <option value="Trace">Trace</option>
                <option value="1+">1+</option>
                <option value="2+">2+</option>
                <option value="3+">3+</option>
                <option value="4+">4+</option>
              </Select>
            </FormGroup>

            <FormGroup>
              <Label>เซลล์เยื่อบุผิว (Epithelial)</Label>
              <Select
                value={editForm.urinalysis_epithelial || ''}
                onChange={(e) => setEditForm({ ...editForm, urinalysis_epithelial: e.target.value })}
              >
                <option value="">-- เลือก --</option>
                <option value="0-1">0-1</option>
                <option value="1-2">1-2</option>
                <option value="2-3">2-3</option>
                <option value="3-5">3-5</option>
                <option value="5-10">5-10</option>
                <option value="10-20">10-20</option>
                <option value="20-30">20-30</option>
                <option value="30-50">30-50</option>
                <option value="50-100">50-100</option>
                <option value="100-200">100-200</option>
                <option value=">200">&gt;200</option>
              </Select>
            </FormGroup>

            <ButtonGroup>
              <CancelButton onClick={handleModalClose}>ยกเลิก</CancelButton>
              <SaveButton onClick={handleEditSubmit}>บันทึก</SaveButton>
            </ButtonGroup>
          </ModalContent>
        </Modal>
      )}

      {alertState.isOpen && (
        <CustomAlert message={alertState.message} onClose={closeAlert} />
      )}
    </TestResultsContainer>
  );
};

export default UrineTestResultsPage;
